/**
 * Admin API for mobile field groups + itinerary
 */
import { supabase } from '../supabase';
import { normalizeSupabaseError } from './errors';
import type {
  EventGroup,
  GroupItineraryItem,
  GroupMember,
  GroupMemberRole,
} from '../../types/database';

export async function listEventGroups(eventId: string): Promise<EventGroup[]> {
  const { data, error } = await supabase
    .from('event_groups')
    .select('*')
    .eq('event_id', eventId)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

export async function getEventGroup(groupId: string): Promise<EventGroup | null> {
  const { data, error } = await supabase
    .from('event_groups')
    .select('*')
    .eq('id', groupId)
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

export async function createEventGroup(
  eventId: string,
  input: { name: string; sort_order?: number }
): Promise<EventGroup> {
  const { data, error } = await supabase
    .from('event_groups')
    .insert({
      event_id: eventId,
      name: input.name.trim(),
      sort_order: input.sort_order ?? 0,
    })
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

export async function updateEventGroup(
  groupId: string,
  updates: Partial<Pick<EventGroup, 'name' | 'sort_order'>>
): Promise<EventGroup> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.name != null) payload.name = updates.name.trim();
  if (updates.sort_order != null) payload.sort_order = updates.sort_order;

  const { data, error } = await supabase
    .from('event_groups')
    .update(payload)
    .eq('id', groupId)
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

export async function deleteEventGroup(groupId: string): Promise<void> {
  const { error } = await supabase.from('event_groups').delete().eq('id', groupId);
  if (error) throw normalizeSupabaseError(error);
}

export async function listGroupMembers(groupId: string): Promise<GroupMember[]> {
  const { data, error } = await supabase
    .from('group_members')
    .select('*')
    .eq('group_id', groupId)
    .order('role', { ascending: true });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

export async function listEventGroupMembers(eventId: string): Promise<GroupMember[]> {
  const { data, error } = await supabase
    .from('group_members')
    .select('*')
    .eq('event_id', eventId);

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

/**
 * Assign an attendee to a group. Moves them if already in another group for this event.
 * Enforces one leader per group by demoting the previous leader when promoting.
 */
export async function assignGroupMember(input: {
  eventId: string;
  groupId: string;
  attendeeProfileId: string;
  role: GroupMemberRole;
}): Promise<GroupMember> {
  const { eventId, groupId, attendeeProfileId, role } = input;

  if (role === 'leader') {
    const { data: existingLeaders, error: leaderErr } = await supabase
      .from('group_members')
      .select('id, attendee_profile_id')
      .eq('group_id', groupId)
      .eq('role', 'leader');
    if (leaderErr) throw normalizeSupabaseError(leaderErr);

    for (const leader of existingLeaders || []) {
      if (leader.attendee_profile_id === attendeeProfileId) continue;
      const { error: demoteErr } = await supabase
        .from('group_members')
        .update({ role: 'member' })
        .eq('id', leader.id);
      if (demoteErr) throw normalizeSupabaseError(demoteErr);
    }
  }

  const { data: existing, error: findErr } = await supabase
    .from('group_members')
    .select('*')
    .eq('event_id', eventId)
    .eq('attendee_profile_id', attendeeProfileId)
    .maybeSingle();
  if (findErr) throw normalizeSupabaseError(findErr);

  if (existing) {
    const { data, error } = await supabase
      .from('group_members')
      .update({ group_id: groupId, role })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw normalizeSupabaseError(error);
    return data;
  }

  const { data, error } = await supabase
    .from('group_members')
    .insert({
      group_id: groupId,
      attendee_profile_id: attendeeProfileId,
      role,
    })
    .select()
    .single();
  if (error) throw normalizeSupabaseError(error);
  return data;
}

export async function updateGroupMemberRole(
  memberId: string,
  groupId: string,
  role: GroupMemberRole
): Promise<GroupMember> {
  if (role === 'leader') {
    const { data: existingLeaders, error: leaderErr } = await supabase
      .from('group_members')
      .select('id')
      .eq('group_id', groupId)
      .eq('role', 'leader');
    if (leaderErr) throw normalizeSupabaseError(leaderErr);

    for (const leader of existingLeaders || []) {
      if (leader.id === memberId) continue;
      const { error: demoteErr } = await supabase
        .from('group_members')
        .update({ role: 'member' })
        .eq('id', leader.id);
      if (demoteErr) throw normalizeSupabaseError(demoteErr);
    }
  }

  const { data, error } = await supabase
    .from('group_members')
    .update({ role })
    .eq('id', memberId)
    .select()
    .single();
  if (error) throw normalizeSupabaseError(error);
  return data;
}

export async function removeGroupMember(memberId: string): Promise<void> {
  const { error } = await supabase.from('group_members').delete().eq('id', memberId);
  if (error) throw normalizeSupabaseError(error);
}

export async function listGroupItinerary(groupId: string): Promise<GroupItineraryItem[]> {
  const { data, error } = await supabase
    .from('group_itinerary_items')
    .select('*')
    .eq('group_id', groupId)
    .order('sort_order', { ascending: true });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

export async function createItineraryItem(
  groupId: string,
  input: {
    title: string;
    notes?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    lat?: number | null;
    lng?: number | null;
    sort_order?: number;
  }
): Promise<GroupItineraryItem> {
  const { data, error } = await supabase
    .from('group_itinerary_items')
    .insert({
      group_id: groupId,
      title: input.title.trim(),
      notes: input.notes?.trim() || null,
      starts_at: input.starts_at || null,
      ends_at: input.ends_at || null,
      lat: input.lat ?? null,
      lng: input.lng ?? null,
      sort_order: input.sort_order ?? 0,
    })
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

export async function updateItineraryItem(
  itemId: string,
  updates: Partial<
    Pick<GroupItineraryItem, 'title' | 'notes' | 'starts_at' | 'ends_at' | 'lat' | 'lng' | 'sort_order'>
  >
): Promise<GroupItineraryItem> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.title != null) payload.title = updates.title.trim();
  if (updates.notes !== undefined) payload.notes = updates.notes?.trim() || null;
  if (updates.starts_at !== undefined) payload.starts_at = updates.starts_at;
  if (updates.ends_at !== undefined) payload.ends_at = updates.ends_at;
  if (updates.lat !== undefined) payload.lat = updates.lat;
  if (updates.lng !== undefined) payload.lng = updates.lng;
  if (updates.sort_order !== undefined) payload.sort_order = updates.sort_order;

  const { data, error } = await supabase
    .from('group_itinerary_items')
    .update(payload)
    .eq('id', itemId)
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

export async function deleteItineraryItem(itemId: string): Promise<void> {
  const { error } = await supabase.from('group_itinerary_items').delete().eq('id', itemId);
  if (error) throw normalizeSupabaseError(error);
}

export async function setEventMobileAppEnabled(
  eventId: string,
  enabled: boolean
): Promise<void> {
  const { error } = await supabase
    .from('events')
    .update({ mobile_app_enabled: enabled })
    .eq('id', eventId);
  if (error) throw normalizeSupabaseError(error);
}
